import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, ArrowLeft } from 'lucide-react';
import { BLOG_POSTS } from '@/data/blogPosts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const BlogList = () => {
  const nav = useNavigate();
  const [allPosts, setAllPosts] = useState(BLOG_POSTS);

  useEffect(() => {
    document.title = 'Blog — GoldenHour | Wedding Photography Website Tips';
    // Fetch auto-generated posts and merge with static
    axios.get(`${API}/blog/posts`).then(r => {
      const autoPosts = r.data || [];
      if (autoPosts.length > 0) {
        // Merge: auto posts first (newest), then static, deduplicate by slug
        const slugs = new Set(autoPosts.map(p => p.slug));
        const combined = [...autoPosts, ...BLOG_POSTS.filter(p => !slugs.has(p.slug))];
        combined.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        setAllPosts(combined);
      }
    }).catch(() => {});
  }, []);

  return (
    <div className="app-page" style={{ minHeight: '100vh' }}>
      <div className="grain" />
      <nav style={{ padding: '16px 5vw', borderBottom: '1px solid var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <a href="/" style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: 'var(--fg)', textDecoration: 'none' }}>golden<em style={{ color: 'var(--gold)' }}>hour</em></a>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(242,239,233,.2)' }}>by Uncut Gems</span>
        </div>
        <a href="/" style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--stone)', textDecoration: 'none', letterSpacing: '.08em' }}>BACK TO HOME</a>
      </nav>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 5vw 100px' }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '12px' }}>Blog</p>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(32px, 5vw, 48px)', marginBottom: '12px', letterSpacing: '-.02em' }}>
          Insights for wedding creatives.
        </h1>
        <p style={{ color: 'var(--stone)', fontSize: '15px', marginBottom: '48px', maxWidth: '500px', lineHeight: 1.7, fontFamily: 'var(--sans)', fontWeight: 300 }}>
          Strategy, SEO, and real advice for photographers and videographers who want more bookings.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {allPosts.map((post) => (
            <article
              key={post.slug}
              data-testid={`blog-card-${post.slug}`}
              onClick={() => nav(`/blog/${post.slug}`)}
              style={{
                padding: '32px 0', borderBottom: '1px solid var(--faint)', cursor: 'pointer',
                transition: 'all .3s',
              }}
              onMouseEnter={e => e.currentTarget.style.paddingLeft = '16px'}
              onMouseLeave={e => e.currentTarget.style.paddingLeft = '0'}
            >
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--accent)' }}>{post.category}</span>
                <span style={{ color: 'var(--faint)' }}>|</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--stone)', display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={10} /> {post.readTime}</span>
              </div>
              <h2 style={{ fontFamily: 'var(--serif)', fontSize: '22px', marginBottom: '8px', lineHeight: 1.3 }}>{post.title}</h2>
              <p style={{ color: 'var(--stone)', fontSize: '14px', lineHeight: 1.6, fontFamily: 'var(--sans)', fontWeight: 300 }}>{post.description}</p>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '12px', fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--accent)', letterSpacing: '.06em' }}>
                READ MORE <ArrowRight size={12} />
              </span>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

const BlogPost = ({ slug }) => {
  const nav = useNavigate();
  const [post, setPost] = useState(BLOG_POSTS.find(p => p.slug === slug) || null);
  const [loading, setLoading] = useState(!post);

  useEffect(() => {
    if (!post) {
      // Try fetching from auto-generated posts
      axios.get(`${API}/blog/posts`).then(r => {
        const found = (r.data || []).find(p => p.slug === slug);
        if (found) setPost(found);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [slug, post]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} — GoldenHour Blog`;
      // Article schema
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'article-schema';
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "description": post.description,
        "datePublished": post.date,
        "author": { "@type": "Organization", "name": "GoldenHour" },
        "publisher": { "@type": "Organization", "name": "GoldenHour", "url": "https://goldenhour.build" },
      });
      document.head.appendChild(script);
      return () => { const el = document.getElementById('article-schema'); if (el) el.remove(); };
    }
  }, [post]);

  if (loading) return (
    <div className="app-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--stone)' }}>Loading...</p>
    </div>
  );

  if (!post) return (
    <div className="app-page" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ fontFamily: 'var(--serif)', fontSize: '32px', marginBottom: '12px' }}>Post not found.</h1>
        <button onClick={() => nav('/blog')} style={{ fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}>Back to blog</button>
      </div>
    </div>
  );

  // Parse markdown-like content to HTML
  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} style={{ fontFamily: 'var(--serif)', fontSize: '24px', marginTop: '40px', marginBottom: '12px' }}>{line.slice(3)}</h2>;
      if (line.startsWith('**Fix:**') || line.startsWith('**Pro tip:**')) {
        const [bold, ...rest] = line.split('**').filter(Boolean);
        return <p key={i} style={{ color: 'var(--fg)', fontSize: '15px', lineHeight: 1.8, marginBottom: '12px' }}><strong style={{ color: 'var(--accent)' }}>{bold}</strong>{rest.join('')}</p>;
      }
      if (line.startsWith('- **')) return <li key={i} style={{ color: 'var(--stone)', fontSize: '15px', lineHeight: 1.8, marginBottom: '4px', marginLeft: '20px' }} dangerouslySetInnerHTML={{ __html: line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong style="color:var(--fg)">$1</strong>') }} />;
      if (line.startsWith('- ')) return <li key={i} style={{ color: 'var(--stone)', fontSize: '15px', lineHeight: 1.8, marginBottom: '4px', marginLeft: '20px' }}>{line.slice(2)}</li>;
      if (line.startsWith('|')) return null; // Tables rendered separately
      if (line.startsWith('---')) return <hr key={i} style={{ border: 'none', borderTop: '1px solid var(--faint)', margin: '32px 0' }} />;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} style={{ color: 'var(--stone)', fontSize: '15px', lineHeight: 1.8, marginBottom: '12px', fontFamily: 'var(--sans)', fontWeight: 300 }} dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong style="color:var(--fg)">$1</strong>') }} />;
    });
  };

  const relatedPosts = BLOG_POSTS.filter(p => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="app-page" style={{ minHeight: '100vh' }}>
      <div className="grain" />
      <nav style={{ padding: '16px 5vw', borderBottom: '1px solid var(--faint)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
          <a href="/" style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: 'var(--fg)', textDecoration: 'none' }}>golden<em style={{ color: 'var(--gold)' }}>hour</em></a>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '8px', letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(242,239,233,.2)' }}>by Uncut Gems</span>
        </div>
        <button onClick={() => nav('/blog')} style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--stone)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '.08em', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <ArrowLeft size={12} /> ALL POSTS
        </button>
      </nav>

      <article style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 5vw 100px' }}>
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--accent)' }}>{post.category}</span>
            <span style={{ color: 'var(--faint)' }}>|</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--stone)' }}>{post.date}</span>
            <span style={{ color: 'var(--faint)' }}>|</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--stone)', display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={10} /> {post.readTime}</span>
          </div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(28px, 5vw, 40px)', lineHeight: 1.2, marginBottom: '16px', letterSpacing: '-.02em' }}>{post.title}</h1>
          <p style={{ color: 'var(--stone)', fontSize: '17px', lineHeight: 1.7, fontFamily: 'var(--sans)', fontWeight: 300 }}>{post.description}</p>
        </div>

        <div style={{ borderTop: '1px solid var(--faint)', paddingTop: '32px' }}>
          {renderContent(post.content)}
        </div>

        {/* CTA */}
        <div style={{ marginTop: '48px', padding: '32px', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '2px', background: 'rgba(201,168,76,0.03)', textAlign: 'center' }}>
          <p style={{ fontFamily: 'var(--serif)', fontSize: '20px', marginBottom: '8px' }}>Ready to build your site?</p>
          <p style={{ color: 'var(--stone)', fontSize: '14px', marginBottom: '20px', fontFamily: 'var(--sans)', fontWeight: 300 }}>See what GoldenHour creates for your brand in 60 seconds. Free, no credit card.</p>
          <a href="/" data-testid="blog-cta" className="btn-accent" style={{ padding: '14px 32px', fontSize: '11px', textDecoration: 'none' }}>Try GoldenHour Free</a>
        </div>

        {/* Related posts */}
        {relatedPosts.length > 0 && (
          <div style={{ marginTop: '64px', borderTop: '1px solid var(--faint)', paddingTop: '32px' }}>
            <p style={{ fontFamily: 'var(--mono)', fontSize: '10px', letterSpacing: '.15em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: '20px' }}>Keep reading</p>
            {relatedPosts.map(rp => (
              <div key={rp.slug} onClick={() => { nav(`/blog/${rp.slug}`); window.scrollTo(0, 0); }} style={{ cursor: 'pointer', padding: '16px 0', borderBottom: '1px solid var(--faint)' }}>
                <p style={{ fontFamily: 'var(--serif)', fontSize: '16px', marginBottom: '4px' }}>{rp.title}</p>
                <span style={{ fontFamily: 'var(--mono)', fontSize: '9px', color: 'var(--accent)' }}>{rp.category} — {rp.readTime}</span>
              </div>
            ))}
          </div>
        )}
      </article>
    </div>
  );
};

export { BlogList, BlogPost };
